import React, { useEffect, useRef, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowCounterClockwiseIcon as ArrowCounterClockwise, UploadSimpleIcon as UploadSimple, LinkSimpleIcon as LinkSimple, ImageIcon as ImageIcon } from "@phosphor-icons/react";

/** Admin-editable About-page hero photos.
 *
 *  Three slots line up with /about — founder card (4:5), mission landscape
 *  (3:2), inside-the-registry square (1:1). Each slot offers two ways to
 *  swap the image: drag/upload a file (admin's own dogs) OR paste any
 *  direct image URL (e.g. a curated stock photo). A single "Reset" link
 *  reverts that slot to the curated default that ships with the codebase.
 */

const SLOTS = [
  {
    key: "founder",
    title: "Founder card",
    blurb: "Portrait orientation (4:5). Sits next to the founder bio on the left side of the hero.",
    aspect: "aspect-[4/5]",
  },
  {
    key: "mission",
    title: "Mission landscape",
    blurb: "Wide landscape (3:2). Anchors the right side of the mission paragraph.",
    aspect: "aspect-[3/2]",
  },
  {
    key: "inside_registry",
    title: "Inside the registry",
    blurb: "Square (1:1). Lives in the \"Inside the registry\" feature grid.",
    aspect: "aspect-square",
  },
];

function slotImageUrl(slotData) {
  if (!slotData?.url) return "";
  // Backend-relative paths get the API origin prepended; absolute http(s)
  // URLs are passed through untouched.
  if (slotData.url.startsWith("/api/")) {
    return `${process.env.REACT_APP_BACKEND_URL}${slotData.url}`;
  }
  return slotData.url;
}

function SlotCard({ slot, data, onChange }) {
  const [urlDraft, setUrlDraft] = useState("");
  const [altDraft, setAltDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setUrlDraft(data?.is_custom ? data.url : "");
    setAltDraft(data?.alt || "");
  }, [data?.url, data?.alt, data?.is_custom]);

  const handleUpload = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      toast.error("Please choose an image file (jpg, png, webp, gif).");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large — max 10MB.");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    if (altDraft) fd.append("alt", altDraft);
    setBusy(true);
    try {
      const { data: updated } = await api.put(`/admin/about/photos/${slot.key}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(slot.key, updated);
      toast.success(`${slot.title} updated.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleUrlSave = async () => {
    if (!urlDraft.trim()) {
      toast.error("Paste an image URL first.");
      return;
    }
    const fd = new FormData();
    fd.append("url", urlDraft.trim());
    if (altDraft) fd.append("alt", altDraft);
    setBusy(true);
    try {
      const { data: updated } = await api.put(`/admin/about/photos/${slot.key}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(slot.key, updated);
      toast.success(`${slot.title} updated.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleReset = async () => {
    setBusy(true);
    try {
      const { data: updated } = await api.delete(`/admin/about/photos/${slot.key}`);
      onChange(slot.key, updated);
      toast.success(`${slot.title} reset to default.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed.");
    } finally {
      setBusy(false);
    }
  };

  const previewUrl = slotImageUrl(data);

  return (
    <div
      className="bg-white border border-[#E5E5E5] rounded-2xl p-5 md:p-6 grid md:grid-cols-[260px_1fr] gap-6"
      data-testid={`about-photo-card-${slot.key}`}
    >
      {/* Preview */}
      <div>
        <div className={`${slot.aspect} rounded-xl bg-[#FBF7EC] border border-[#E8D9A6] overflow-hidden relative`}>
          {previewUrl ? (
            <img
              src={previewUrl}
              alt={data?.alt || ""}
              className="w-full h-full object-cover"
              data-testid={`about-photo-preview-${slot.key}`}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#9A9A9A]">
              <ImageIcon size={36} weight="duotone" />
            </div>
          )}
          {data?.is_custom && (
            <span
              className="absolute top-2 left-2 text-[10px] uppercase tracking-[0.2em] font-bold bg-[#0E0E0E] text-gold px-2 py-1 rounded-full"
              data-testid={`about-photo-custom-badge-${slot.key}`}
            >
              Custom
            </span>
          )}
        </div>
        {data?.is_custom && (
          <button
            type="button"
            onClick={handleReset}
            disabled={busy}
            className="mt-3 text-xs text-[#6B6B6B] hover:text-crimson inline-flex items-center gap-1 underline-offset-2 hover:underline"
            data-testid={`about-photo-reset-${slot.key}`}
          >
            <ArrowCounterClockwise size={12} weight="bold" /> Reset to default
          </button>
        )}
      </div>

      {/* Controls */}
      <div className="flex flex-col gap-5 min-w-0">
        <div>
          <h3 className="font-spectral text-xl tracking-tight">{slot.title}</h3>
          <p className="text-sm text-[#6B6B6B] mt-1 leading-relaxed">{slot.blurb}</p>
        </div>

        <div>
          <Label className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#3F3F3F]">
            Alt text
          </Label>
          <Input
            value={altDraft}
            onChange={(e) => setAltDraft(e.target.value)}
            placeholder="Describe the image (used by screen readers and SEO)"
            className="mt-2"
            data-testid={`about-photo-alt-${slot.key}`}
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          {/* Upload */}
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#3F3F3F]">
              Upload your own
            </Label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                handleUpload(e.target.files?.[0]);
                e.target.value = "";
              }}
              data-testid={`about-photo-file-input-${slot.key}`}
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
              className="mt-2 w-full rounded-full border-[#E5E5E5] h-10"
              data-testid={`about-photo-upload-btn-${slot.key}`}
            >
              <UploadSimple size={16} className="mr-2" weight="duotone" />
              {busy ? "Working…" : "Choose file"}
            </Button>
            <p className="text-[11px] text-[#9A9A9A] mt-1.5">JPG, PNG, WebP, GIF · up to 10MB.</p>
          </div>

          {/* URL */}
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#3F3F3F]">
              Or paste an image URL
            </Label>
            <Input
              value={urlDraft}
              onChange={(e) => setUrlDraft(e.target.value)}
              placeholder="https://…"
              className="mt-2"
              data-testid={`about-photo-url-input-${slot.key}`}
            />
            <Button
              type="button"
              onClick={handleUrlSave}
              disabled={busy || !urlDraft.trim()}
              className="mt-2 w-full rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white h-10"
              data-testid={`about-photo-url-save-${slot.key}`}
            >
              <LinkSimple size={16} className="mr-2" weight="duotone" />
              {busy ? "Saving…" : "Use this URL"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminAboutPhotos() {
  const [photos, setPhotos] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/about/photos")
      .then((r) => setPhotos(r.data))
      .catch(() => toast.error("Couldn't load About photos."))
      .finally(() => setLoading(false));
  }, []);

  const onChange = (key, updated) => {
    setPhotos((prev) => ({ ...(prev || {}), [key]: updated }));
  };

  return (
    <AppShell>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-2">
            Admin · About page
          </p>
          <h1 className="font-spectral text-4xl sm:text-5xl tracking-tight">
            About-page photos
          </h1>
          <p className="text-sm text-[#6B6B6B] mt-3 max-w-2xl">
            Swap the three hero photos on <a href="/about" className="underline-offset-2 hover:underline" target="_blank" rel="noreferrer">/about</a>. Upload your own dogs, paste a different stock URL, or revert to the curated defaults at any time.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-[#6B6B6B]">Loading…</div>
      ) : (
        <div className="space-y-5" data-testid="about-photo-cards">
          {SLOTS.map((slot) => (
            <SlotCard key={slot.key} slot={slot} data={photos?.[slot.key]} onChange={onChange} />
          ))}
        </div>
      )}
    </AppShell>
  );
}
